import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { interval, Observable, Subscriber, timer } from 'rxjs';
import { Task } from '../../shared/model/task';
import { environment } from '../../../environments/environment';
import { RequestFilter } from '../../shared/model/request-filter';
import { TaskSnapshot } from '../../shared/model/task-snapshot';

import { saveAs } from 'file-saver';
import { FileInfo } from '../../shared/model/file-info';
import { CreateRequestRequest } from '../../shared/model/request/create-request-request';
import { ActiveTask } from '../../shared/model/active-task';
import {catchError, mergeMap, take} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';
import {TaskQueueItem} from "../model/TaskQueueItem";
import {LogEntry} from '../model/log-entry';

@Injectable()
export class TaskService {

  constructor(public _http: HttpClient) { }


  /**
   * Get an array of tasks
   * @param {RequestFilter} filter Optional filter that allows filtering the received data
   * @returns {Observable<Task[]>}
   */
  getTasks(filter?: RequestFilter): Observable<Task[]> {
    // States property has to be set
    if (filter && !filter.states) {
      filter.states = [];
    } else if (!filter) {
      filter = { states: [] };
    }

    filter.workFlows = ['WF2'];

    return new Observable<Task[]>((subscriber: Subscriber<Task[]>) => {
      this._http.post(environment.endpoints.clientBaseUrl + environment.endpoints.taskList, filter)
        .subscribe(
          (tasks: Task[]) => {
            subscriber.next(tasks);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Get all active Tasks
   * @returns {Observable<ActiveTask[]>}
   */
  getActiveTasks(): Observable<ActiveTask[]> {
    return new Observable<ActiveTask[]>((subscriber: Subscriber<ActiveTask[]>) => {
      this._http.get<ActiveTask[]>(environment.endpoints.clientBaseUrl + environment.endpoints.getActiveTasks)
        .subscribe(
          (activeTask: ActiveTask[]) => {
            subscriber.next(activeTask);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Get a single {@link Task} by it's ID
   * @param {number} taskId The task ID
   * @returns {Observable<Task>}
   */
  getTask(taskId: number): Observable<Task> {
    return new Observable<Task>((subscriber: Subscriber<Task>) => {
      this._http.get(environment.endpoints.clientBaseUrl + environment.endpoints.getTask.replace('{id}', String(taskId)))
        .subscribe(
          (task: Task) => {
            subscriber.next(task);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Create a new {@link Task}
   * @param {CreateRequestRequest} request Information about the new {@link Task}
   * @returns {Observable<Task>}
   */
  createTask(request: CreateRequestRequest): Observable<Task> {
    return new Observable<Task>((subscriber: Subscriber<Task>) => {
      this._http.post(environment.endpoints.clientBaseUrl + environment.endpoints.createTask, request)
        .subscribe(
          (task: Task) => {
            subscriber.next(task);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Get a snapshot of the current state of a {@link Task}
   * @param {number} taskId The task ID
   * @returns {Observable<TaskSnapshot>}
   */
  getTaskSnapshot(taskId: number): Observable<TaskSnapshot> {
    return new Observable<TaskSnapshot>((subscriber: Subscriber<TaskSnapshot>) => {
      this._http.get(environment.endpoints.clientBaseUrl + environment.endpoints.taskSnapshot.replace('{id}', String(taskId)))
        .subscribe(
          (snapshot: TaskSnapshot) => {
            subscriber.next(snapshot);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Start a {@link Task} by it's ID
   * @param {number} task The task to start
   * @returns {Observable<Task>}
   */
  startTask(task: Task): Observable<Task> {
    return new Observable<any>((subscriber: Subscriber<any>) => {
      this._http.post(environment.endpoints.clientBaseUrl + environment.endpoints.startTask.replace('{id}', String(task.taskId)), task)
        .subscribe(
          (response: Task) => {
            subscriber.next(response);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

/*  _downloadInputFileAsBlob(taskId: number): Observable<FileInfo> {

    return new Observable<FileInfo>((subscriber: Subscriber<FileInfo>) => {
      const url = environment.endpoints.clientBaseUrl + environment.endpoints.downloadResults
        .replace('{taskId}', String(taskId));

      this._http.get(url, { responseType: 'blob', observe: 'response' })
        .subscribe(
          (response: HttpResponse<Blob>) => {

            // Get filename
            let fileName = response.headers.get('Content-Disposition');
            fileName = fileName.split('=')[1];

            subscriber.next({ data: response.body, fileName: fileName });
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }*/

/*  /!**
   * Download the input file of a {@link Task}
   * @param {number} taskId The task's ID
   * @returns {Observable<any>}
   *!/
  downloadInputFile(taskId: number): Observable<FileInfo> {
    // TODO: Download input file (needs WS work)

    return new Observable<FileInfo>((subscriber: Subscriber<FileInfo>) => {
      this._downloadInputFileAsBlob(taskId)
        .subscribe(
          (fileInfo: FileInfo) => {
            saveAs(fileInfo.data, fileInfo.fileName);
            subscriber.next(fileInfo);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }*/

  _downloadResultsAsBlob(requestDefinitionId: number, taskId: number): Observable<FileInfo> {
    return new Observable<FileInfo>((subscriber: Subscriber<FileInfo>) => {
      const url = environment.endpoints.clientBaseUrl + environment.endpoints.downloadResults
        .replace('{rdId}', String(requestDefinitionId))
        .replace('{taskId}', String(taskId));

      this._http.get(url, { responseType: 'blob', observe: 'response' })
        .subscribe(
          (response: HttpResponse<Blob>) => {

            // Error response
            if (!response.ok) {
              subscriber.error(response.body);
              return;
            }

            // Get filename
            let fileName = response.headers.get('Content-Disposition');
            fileName = fileName.split('=')[1];

            subscriber.next({ data: response.body, fileName: fileName });
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Download the results of a finished {@link Task}
   * @param {number} requestDefinitionId The task's request definition ID
   * @param {number} taskId The task's ID
   * @returns {Observable<any>}
   */
  downloadResults(requestDefinitionId: number, taskId: number): Observable<FileInfo> {
    return new Observable<FileInfo>((subscriber: Subscriber<FileInfo>) => {
      this._downloadResultsAsBlob(requestDefinitionId, taskId)
        .subscribe(
          (fileInfo: FileInfo) => {

            saveAs(fileInfo.data, fileInfo.fileName);
            subscriber.next(fileInfo);
            subscriber.complete();
          },
          (error: { errorCode: string; errorMessage: string }) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Update a {@link Task}
   * @param {Task} task The new {@link Task}
   * @returns {Observable<Task>}
   */
  updateTask(task: Task): Observable<Task> {
    return new Observable<Task>((subscriber: Subscriber<Task>) => {
      this._http.put(environment.endpoints.clientBaseUrl + environment.endpoints.updateTask, task)
        .subscribe(
          (updated: Task) => {
            subscriber.next(updated);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }

  /**
   * Accept a request (Task)
   * @param (requestId) The request (Task) ID
   * @returns (Observable<Task>) The approved request
   */
  acceptRequest(requestId): Observable<Task> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.approveRequest
      .replace('{id}', String(requestId));

    return this._http.get<Task>(url);
  }

  /**
   * Denies a request (Task)
   * @param (requestId) The request (Task) ID
   * @returns (Observable<Task>) The denied request
   */
  denyRequest(requestId: number): Observable<Task> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.denyRequest
      .replace('{id}', String(requestId));

    return this._http.get<Task>(url);
  }

  /**
   * Cancels a request (Task)
   * @param (requestId) The request (Task) ID
   * @returns (Observable<Task>) The canceled request
   */
  cancelRequest(requestId: number): Observable<Task> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.cancelRequest
      .replace('{id}', String(requestId));

    return this._http.get<Task>(url);
  }

  /**
   * Get all sub tasks of a request
   * @param (requestId) The ID of the requests to get the sub tasks of
   */
  getRequestSubtasks(requestId: number): Observable<Task[]> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.getRequestSubtasks
      .replace('{id}', String(requestId));

    return this._http.get<Task[]>(url);
  }


  /**
   * Multiple backend calls in a period of time with delay
   * to load the latest subtasks for the given request ID
   * @param requestId the request ID for the subtasks
   */
  getRequestSubtasksDelayed(requestId: number): Observable<Task[]> {
    return interval(3000)
      .pipe(
        mergeMap(() => {
          console.log('Update subtasks');
          return this.getRequestSubtasks(requestId);
        }),
      );
  }

  getQueuedTasks(clientName?: string, executionProperty?: string): Observable<TaskQueueItem[]> {
    const url = environment.endpoints.clientBaseUrl + environment.endpoints.getQueuedTasks
      .replace('{client}', String(clientName))
      .replace('{execProp}', String(executionProperty));
    return new Observable<TaskQueueItem[]>((subscriber: Subscriber<TaskQueueItem[]>) => {
      this._http.get(url)
        .subscribe(
          (taskQueueEntries: TaskQueueItem[]) => {
            subscriber.next(taskQueueEntries);
            subscriber.complete();
          },
          (error: any) => {
            subscriber.error(error);
          }
        );
    });
  }
}
